import { getUniqueId } from "../../../helpers";

export interface ILabelItem {
  id: string;
  value: string;
}

export interface ILabelGroup {
  id: string;
  label: string;
  items: ILabelItem[];
}

/**
 * Parse a raw DB string (JSON array or old HTML) into ILabelGroup[].
 * Falls back to a single empty group if parsing fails (backward compat).
 */
export const parseLabelGroups = (raw: string | ILabelGroup[] | null | undefined): ILabelGroup[] => {
  const emptyGroup = () => [{ id: getUniqueId(), label: "", items: [{ id: getUniqueId(), value: "" }] }];

  if (!raw) return emptyGroup();

  // Already an array (JSON DB field returns parsed object directly)
  if (Array.isArray(raw)) {
    if (raw.length === 0) return emptyGroup();
    return raw.map((g: any) => ({
      id: g.id || getUniqueId(),
      label: g.label || "",
      items: Array.isArray(g.items)
        ? g.items.map((item: any) => ({ id: item.id || getUniqueId(), value: item.value || "" }))
        : [{ id: getUniqueId(), value: "" }],
    }));
  }

  if (typeof raw !== "string" || raw.trim() === "") return emptyGroup();

  const trimmed = raw.trim();

  // Try JSON parse (string that contains JSON array)
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((g: any) => ({
          id: g.id || getUniqueId(),
          label: g.label || "",
          items: Array.isArray(g.items)
            ? g.items.map((item: any) => ({ id: item.id || getUniqueId(), value: item.value || "" }))
            : [{ id: getUniqueId(), value: "" }],
        }));
      }
    } catch {
      // fall through to HTML handling
    }
  }

  // Old HTML format — extract text lines as a single group's items
  const lines = htmlToTextLines(trimmed);
  if (lines.length > 0) {
    return [
      {
        id: getUniqueId(),
        label: "",
        items: lines.map((line) => ({ id: getUniqueId(), value: line })),
      },
    ];
  }

  return emptyGroup();
};

/**
 * Serialize ILabelGroup[] for API storage.
 * DB field is JSON type — returns the cleaned array directly (not a string).
 * Filters out completely empty groups and items.
 */
export const serializeLabelGroups = (groups: ILabelGroup[]): ILabelGroup[] => {
  return groups
    .filter((g) => g.label.trim() || g.items.some((i) => i.value.trim()))
    .map((g) => ({
      id: g.id,
      label: g.label,
      items: g.items.filter((i) => i.value.trim()),
    }));
};

/**
 * Convert ILabelGroup[] to HTML string for PDF rendering.
 * Produces: <p><strong>GROUP LABEL:</strong></p><p>item 1</p><p>item 2</p>...
 * Compatible with both HtmlContent (react-pdf-html) and htmlToLines() strip-based parsers.
 */
export const labelGroupsToHtml = (groups: ILabelGroup[]): string => {
  return groups
    .filter((g) => g.label.trim() || g.items.some((i) => i.value.trim()))
    .map((g) => {
      const heading = g.label.trim()
        ? `<p><strong>${escapeHtml(g.label.trim())}:</strong></p>`
        : "";
      const items = g.items
        .filter((i) => i.value.trim())
        .map((i) => `<p>${escapeHtml(i.value.trim())}</p>`)
        .join("");
      return heading + items;
    })
    .join("");
};

/**
 * Parse a raw inclusion/exclusion DB value for PDF rendering.
 * Handles: direct array (JSON DB field), JSON string, or old HTML string.
 */
export const parseInclusionForPdf = (raw: string | ILabelGroup[] | null | undefined): string => {
  if (!raw) return "";

  // Direct array from JSON DB field
  if (Array.isArray(raw)) {
    return labelGroupsToHtml(raw);
  }

  if (typeof raw !== "string" || raw.trim() === "") return "";

  const trimmed = raw.trim();
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return labelGroupsToHtml(parsed as ILabelGroup[]);
      }
    } catch {
      // fall through
    }
  }
  // Already HTML — return as-is
  return raw;
};

/**
 * Parse the `payment_mode_policies` HTML (produced by ProposalEditor: an overall
 * heading + a list of paragraphs, each with an optional bold title) for PDF rendering.
 *
 * Paragraphs with NO title (typed directly, e.g. "Bank Name: HDFC BANK") are treated
 * as plain bank-detail lines. Paragraphs WITH a title (e.g. "1st Payment") are treated
 * as payment terms and rendered as "Title: description" bullet lines.
 */
export interface IParsedPaymentPolicies {
  heading: string;
  bankLines: string[];
  paymentTerms: string[];
}

export const parsePaymentPoliciesForPdf = (
  raw: string | null | undefined
): IParsedPaymentPolicies => {
  const empty: IParsedPaymentPolicies = { heading: "", bankLines: [], paymentTerms: [] };
  if (!raw || typeof raw !== "string" || !raw.trim()) return empty;

  // Flatten into individual logical lines. A <p> is usually one line, but the
  // editor sometimes stores several lines inside a single <p> separated by
  // <br> (e.g. a plain multi-line field instead of one <p> per line) — split
  // those out too, otherwise they'd all get flattened into one run-on line below.
  const blocks: string[] = [];
  const blockRe = /<p[^>]*>([\s\S]*?)<\/p>/gi;
  let match: RegExpExecArray | null;
  while ((match = blockRe.exec(raw)) !== null) {
    match[1]
      .split(/<br\s*\/?>/gi)
      .map((l) => l.trim())
      .filter((l) => l !== "")
      .forEach((l) => blocks.push(l));
  }

  if (blocks.length === 0) {
    return { heading: "", bankLines: htmlToTextLines(raw), paymentTerms: [] };
  }

  // A line that is ENTIRELY a <strong> tag (used for the overall heading, and
  // for a payment-term title that has its description on the following line).
  const strongOnlyText = (line: string): string | null => {
    const trimmed = line.trim();
    const m = trimmed.match(/^<strong>([\s\S]*?)<\/strong>$/i);
    return m ? m[1].replace(/<[^>]*>/g, "").trim() : null;
  };

  // A line that STARTS with a <strong> tag followed by more text on the same
  // line (e.g. "<strong>1st Payment:</strong> Token amount..."). Returns the
  // title/rest split, or null if the line doesn't lead with a <strong> tag.
  const leadingStrongText = (line: string): { title: string; rest: string } | null => {
    const trimmed = line.trim();
    const m = trimmed.match(/^<strong>([\s\S]*?)<\/strong>\s*([\s\S]*)$/i);
    if (!m) return null;
    return { title: m[1].replace(/<[^>]*>/g, "").trim(), rest: stripTagsToText(m[2]) };
  };

  let idx = 0;
  let heading = "";
  const firstStrong = strongOnlyText(blocks[0]);
  if (firstStrong !== null) {
    heading = firstStrong;
    idx = 1;
  }

  const bankLines: string[] = [];
  const paymentTerms: string[] = [];

  while (idx < blocks.length) {
    const leading = leadingStrongText(blocks[idx]);
    if (leading !== null) {
      const title = leading.title.replace(/:\s*$/, "").trim();
      if (leading.rest) {
        // Title and description share one line, e.g. "<strong>1st Payment:</strong> desc"
        paymentTerms.push(`${title}: ${leading.rest}`);
        idx++;
      } else {
        // Title alone — gather subsequent non-title lines as its description
        idx++;
        const descParts: string[] = [];
        while (idx < blocks.length && leadingStrongText(blocks[idx]) === null) {
          const line = stripTagsToText(blocks[idx]);
          if (line) descParts.push(line);
          idx++;
        }
        const desc = descParts.join(" ");
        paymentTerms.push(desc ? `${title}: ${desc}` : title);
      }
    } else {
      const line = stripTagsToText(blocks[idx]);
      if (line) bankLines.push(line);
      idx++;
    }
  }

  return { heading, bankLines, paymentTerms };
};

const stripTagsToText = (html: string): string => {
  let text = html.replace(/<br\s*\/?>/gi, " ");
  text = text.replace(/<[^>]*>/g, "");
  text = text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"');
  return text.trim();
};

// ─── Internal helpers ────────────────────────────────────────────────────────

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const htmlToTextLines = (html: string): string[] => {
  let text = html.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<\/p>/gi, "\n");
  text = text.replace(/<\/li>/gi, "\n");
  text = text.replace(/<[^>]*>/g, "");
  text = text.replace(/&amp;/g, "&");
  text = text.replace(/&lt;/g, "<");
  text = text.replace(/&gt;/g, ">");
  text = text.replace(/&nbsp;/g, " ");
  text = text.replace(/&quot;/g, '"');
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
};
